import { Component, computed, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { Observable } from "rxjs";

import {
  MantenimientoModulosRes,
  ModuloEnMantenimiento,
  ModuloOpcion,
} from "src/app/shared/model/mantenimiento-modulos/mantenimiento-modulos.model";
import { FECHA_ESTANDAR_FORMATS, FechaEstandarAdapter } from "src/app/utilerias/fecha-estandar.adapter";
import { MantenimientoModulosService } from "../mantenimiento-modulos.service";
import { MENSAJE_POR_OMISION, textoAviso } from "../reglas";

export interface DatosDialogoMantenimiento {
  opcion: ModuloOpcion;
  /** Si ya está en mantenimiento: se cambia su aviso. */
  actual?: ModuloEnMantenimiento;
}

/** Una hora de la lista: «18:30», y si ya pasó (el día escogido es hoy). */
export interface HoraRegreso {
  valor: string;
  pasada: boolean;
}

const LARGO_MENSAJE = 500;
const ID_TITULO = "mmDialogoTitulo";

const dos = (n: number) => ("0" + n).slice(-2);

/** Las horas que se pueden escoger: cada media hora. */
const HORAS = Array.from({ length: 48 }, (_, i) => `${dos(Math.floor(i / 2))}:${i % 2 === 0 ? "00" : "30"}`);

/** AAAA-MM-DD de un día, en hora local. */
function diaTexto(d: Date): string {
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
}

/** AAAA-MM-DDTHH:mm de ahora, en hora local (como lo manda el diálogo al servidor). */
function ahoraLocal(): string {
  const d = new Date();
  return `${diaTexto(d)}T${dos(d.getHours())}:${dos(d.getMinutes())}`;
}

/** Hoy a las 00:00: el calendario no deja escoger días anteriores. */
function inicioDeHoy(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** El día de «AAAA-MM-DD HH:mm», o null. */
function diaDe(valor: string | null | undefined): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(valor || "");
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/** La hora «HH:mm» de «AAAA-MM-DD HH:mm», o vacía. */
function horaDe(valor: string | null | undefined): string {
  const m = /[ T](\d{2}:\d{2})/.exec(valor || "");
  return m ? m[1] : "";
}

/**
 * Poner un módulo en mantenimiento o cambiarle el aviso (16/10/2026). Guarda
 * él mismo: si el servidor rechaza (una fecha que ya pasó, por ejemplo), se
 * queda abierto con el motivo; si sale bien, se cierra con lo que quedó. La
 * fecha de regreso se escoge en un calendario que no deja días anteriores a
 * hoy, y la hora en una lista cada media hora sin las que ya pasaron.
 */
@Component({
  selector: "app-dialogo-mantenimiento",
  imports: [
    FormsModule,
    MatDialogContent,
    MatDialogActions,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: "es-MX" },
    { provide: DateAdapter, useClass: FechaEstandarAdapter },
    { provide: MAT_DATE_FORMATS, useValue: FECHA_ESTANDAR_FORMATS },
  ],
  templateUrl: "./dialogo-mantenimiento.component.html",
  styleUrl: "./dialogo-mantenimiento.component.css",
})
export class DialogoMantenimientoComponent {
  readonly datos = inject<DatosDialogoMantenimiento>(MAT_DIALOG_DATA);
  private readonly ref = inject<MatDialogRef<DialogoMantenimientoComponent, MantenimientoModulosRes>>(MatDialogRef);
  private readonly api = inject(MantenimientoModulosService);

  readonly idTitulo = ID_TITULO;
  readonly largoMensaje = LARGO_MENSAJE;
  readonly porOmision = MENSAJE_POR_OMISION;
  readonly hoy = inicioDeHoy();
  readonly esCambio = !!this.datos.actual;

  readonly mensaje = signal((this.datos.actual?.mensaje || "").trim());
  readonly conFecha = signal(!!this.datos.actual?.hasta);
  readonly dia = signal<Date | null>(diaDe(this.datos.actual?.hasta));
  readonly hora = signal(horaDe(this.datos.actual?.hasta));
  readonly enviando = signal(false);
  readonly error = signal("");

  /** La fecha de regreso como la manda el diálogo (AAAA-MM-DDTHH:mm), o vacía si falta el día o la hora. */
  readonly hasta = computed(() => {
    const dia = this.dia();
    return dia && this.hora() ? `${diaTexto(dia)}T${this.hora()}` : "";
  });

  /**
   * La lista de horas; si el día es hoy, las que ya pasaron no se pueden
   * escoger. Una hora guardada antes que no cae en la media hora se agrega.
   */
  readonly horas = computed<HoraRegreso[]>(() => {
    const dia = this.dia();
    const ahora = ahoraLocal();
    const esHoy = !!dia && diaTexto(dia) === ahora.slice(0, 10);
    const lista = HORAS.includes(this.hora()) || !this.hora() ? HORAS : [...HORAS, this.hora()].sort((x, y) => x.localeCompare(y));
    return lista.map((valor) => ({ valor, pasada: esHoy && valor <= ahora.slice(11) }));
  });

  /** Cómo lo verá quien quiera entrar. */
  readonly vistaPrevia = computed(() =>
    textoAviso({ clave: "", nombre: "", mensaje: this.mensaje(), hasta: this.conFecha() ? this.hasta() : null }),
  );

  cambiaMensaje(valor: string): void {
    this.mensaje.set(valor);
    this.error.set("");
  }

  cambiaFecha(conFecha: boolean): void {
    this.conFecha.set(conFecha);
    this.error.set("");
  }

  /** El día del calendario; si la hora escogida ya pasó ese día (hoy), se borra para escoger otra. */
  cambiaDia(valor: Date | null): void {
    this.dia.set(valor);
    if (this.horas().some((h) => h.pasada && h.valor === this.hora())) {
      this.hora.set("");
    }
    this.error.set("");
  }

  cambiaHora(valor: string): void {
    this.hora.set(valor || "");
    this.error.set("");
  }

  cancelar(): void {
    this.ref.close();
  }

  guardar(): void {
    if (this.enviando()) {
      return;
    }
    if (this.conFecha() && !this.hasta()) {
      this.error.set("Escoge el día y la hora en que vuelve a estar disponible.");
      return;
    }
    if (this.conFecha() && this.hasta() <= ahoraLocal()) {
      this.error.set("La fecha de regreso tiene que ser después de este momento.");
      return;
    }
    this.enviando.set(true);
    this.api
      .guardar({
        clave: this.datos.opcion.clave,
        mensaje: this.mensaje().trim(),
        hasta: this.conFecha() ? this.hasta() : null,
      })
      .subscribe({
        next: (res) => {
          this.enviando.set(false);
          if (!res || res.codigoError) {
            this.error.set(res?.mensajeError || "No se pudo guardar. Inténtalo de nuevo.");
            return;
          }
          this.ref.close(res);
        },
        error: () => {
          this.enviando.set(false);
          this.error.set("No se pudo guardar. Revisa tu conexión e inténtalo de nuevo.");
        },
      });
  }
}

/** Abre el diálogo; da lo que quedó si se guardó. */
export function abrirDialogoMantenimiento(
  dialog: MatDialog,
  datos: DatosDialogoMantenimiento,
): Observable<MantenimientoModulosRes | undefined> {
  return dialog
    .open<DialogoMantenimientoComponent, DatosDialogoMantenimiento, MantenimientoModulosRes>(DialogoMantenimientoComponent, {
      data: datos,
      width: "560px",
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(100vh - 32px)",
      panelClass: "app-standard-dialog",
      backdropClass: "app-standard-dialog-backdrop",
      ariaLabelledBy: ID_TITULO,
      autoFocus: "#mmDialogoMensaje",
    })
    .afterClosed();
}
